import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
    Dialog, 
    DialogContent, 
    DialogDescription, 
    DialogHeader, 
    DialogTitle, 
    DialogTrigger,
    DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
    Plus, Search, FolderOpen, Clock, MoreVertical, 
    Trash2, ExternalLink, Loader2, LogOut, Code2, Rocket, Github, Check, AlertCircle, Pencil 
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const Dashboard = () => {
    const { user, signOut } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newProject, setNewProject] = useState({ name: '', description: '' });
    const [creating, setCreating] = useState(false);
    const [githubStatus, setGithubStatus] = useState({ connected: false, username: null });
    const [githubMessage, setGithubMessage] = useState(null);
    
    // Rename project state
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [renameProject, setRenameProject] = useState({ id: null, name: '' });
    const [renaming, setRenaming] = useState(false);

    // Handle GitHub OAuth callback
    useEffect(() => {
        const handleGitHubAuth = async () => {
            const githubAuth = searchParams.get('github_auth');
            const githubError = searchParams.get('github_error');
            
            if (githubError) {
                setGithubMessage({ type: 'error', text: decodeURIComponent(githubError) });
                // Clear URL params
                setSearchParams({});
                setTimeout(() => setGithubMessage(null), 5000);
                return;
            }
            
            if (githubAuth) {
                try {
                    const githubData = JSON.parse(decodeURIComponent(githubAuth));
                    
                    // Save GitHub connection to backend
                    await apiService.connectGitHub({
                        github_token: githubData.token,
                        github_user: {
                            id: githubData.id,
                            login: githubData.login,
                            name: githubData.name,
                            avatar_url: githubData.avatar_url
                        }
                    });
                    
                    setGithubStatus({ connected: true, username: githubData.login });
                    setGithubMessage({ type: 'success', text: `GitHub conectat ca @${githubData.login}` });
                    
                    // Clear URL params
                    setSearchParams({});
                    setTimeout(() => setGithubMessage(null), 5000);
                    
                } catch (error) {
                    console.error('Error connecting GitHub:', error);
                    setGithubMessage({ type: 'error', text: 'Eroare la conectarea GitHub' });
                    setSearchParams({});
                    setTimeout(() => setGithubMessage(null), 5000);
                }
            }
        };
        
        handleGitHubAuth();
    }, [searchParams, setSearchParams]);

    // Check GitHub status on load
    useEffect(() => {
        const checkGithubStatus = async () => {
            try {
                const { data } = await apiService.getGitHubStatus();
                setGithubStatus({ connected: data.connected, username: data.github_username });
            } catch (error) {
                console.error('Error checking GitHub status:', error);
            }
        };
        
        if (user) {
            checkGithubStatus();
        }
    }, [user]);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        fetchProjects();
    }, [user, navigate]);

    const fetchProjects = async () => {
        try {
            const { data } = await apiService.getProjects();
            setProjects(data);
        } catch (error) {
            console.error('Error fetching projects:', error);
            // Don't block UI on error, just show empty state
            setProjects([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateProject = async () => {
        if (!newProject.name.trim()) return;
        
        setCreating(true);
        try {
            const { data } = await apiService.createProject(newProject);
            setProjects([data, ...projects]);
            setCreateDialogOpen(false);
            setNewProject({ name: '', description: '' });
            navigate(`/editor/${data.id}`);
        } catch (error) {
            console.error('Error creating project:', error);
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteProject = async (projectId) => {
        if (!window.confirm('Are you sure you want to delete this project?')) return;
        
        try {
            await apiService.deleteProject(projectId);
            setProjects(projects.filter(p => p.id !== projectId));
        } catch (error) {
            console.error('Error deleting project:', error);
        }
    };

    const handleRenameProject = async () => {
        if (!renameProject.name.trim() || !renameProject.id) return;
        
        setRenaming(true);
        try {
            await apiService.updateProject(renameProject.id, { name: renameProject.name.trim() });
            setProjects(projects.map(p => 
                p.id === renameProject.id 
                    ? { ...p, name: renameProject.name.trim() } 
                    : p
            ));
            setRenameDialogOpen(false);
            setRenameProject({ id: null, name: '' });
        } catch (error) {
            console.error('Error renaming project:', error);
        } finally {
            setRenaming(false);
        }
    };

    const openRenameDialog = (project) => {
        setRenameProject({ id: project.id, name: project.name });
        setRenameDialogOpen(true);
    };

    const filteredProjects = projects.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Navbar */}
            <nav className="bg-white border-b border-gray-200 px-6 py-3 sticky top-0 z-50">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <img 
                            src="https://blog.mersi.ai/wp-content/uploads/2025/05/mersi.ai-logo-1024px.jpg" 
                            alt="Mersi.ai Logo" 
                            className="w-9 h-9 rounded object-cover"
                        />
                        <span className="font-heading text-lg text-gray-900">VibeCoder</span>
                    </Link>
                    
                    <div className="flex items-center gap-4">
                        {/* GitHub Status Badge */}
                        {githubStatus.connected && (
                            <div className="hidden md:flex items-center gap-2 px-3 py-1 bg-green-50 border border-green-200 rounded-full">
                                <Github className="w-4 h-4 text-green-600" />
                                <span className="text-sm text-green-700">@{githubStatus.username}</span>
                            </div>
                        )}
                        
                        <span className="text-sm text-gray-500 hidden md:block">
                            {user?.email}
                        </span>
                        <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={signOut}
                            className="text-gray-600 hover:text-gray-900"
                            data-testid="signout-btn"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Sign Out
                        </Button>
                    </div>
                </div>
            </nav>

            {/* GitHub Notification */}
            {githubMessage && (
                <div className={`fixed top-20 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg ${
                    githubMessage.type === 'success' 
                        ? 'bg-green-50 border border-green-200 text-green-700' 
                        : 'bg-red-50 border border-red-200 text-red-700'
                }`}>
                    {githubMessage.type === 'success' 
                        ? <Check className="w-4 h-4" /> 
                        : <AlertCircle className="w-4 h-4" />
                    }
                    <span className="text-sm">{githubMessage.text}</span>
                </div>
            )}

            {/* Main Content */}
            <main className="max-w-6xl mx-auto px-6 py-10">
                {/* Header */}
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="font-heading text-2xl md:text-3xl text-gray-900 mb-1">
                            Proiecte
                        </h1>
                        <p className="text-gray-500">
                            Build and deploy amazing apps with AI
                        </p>
                    </div>
                    
                    <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-project-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                New Project
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white border-gray-200">
                            <DialogHeader>
                                <DialogTitle className="font-heading text-gray-900">Create New Project</DialogTitle>
                                <DialogDescription className="text-gray-500">
                                    Give your project a name and description to get started.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                    <Label htmlFor="project-name" className="text-gray-700">Project Name</Label>
                                    <Input
                                        id="project-name"
                                        placeholder="My Awesome App"
                                        value={newProject.name}
                                        onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                                        className="border-gray-300"
                                        data-testid="project-name-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="project-description" className="text-gray-700">Description (optional)</Label>
                                    <Textarea
                                        id="project-description"
                                        placeholder="A brief description of your project..."
                                        value={newProject.description}
                                        onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                                        className="border-gray-300 resize-none"
                                        rows={3}
                                        data-testid="project-description-input"
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="border-gray-300">
                                    Cancel
                                </Button>
                                <Button 
                                    onClick={handleCreateProject} 
                                    disabled={creating || !newProject.name.trim()}
                                    className="bg-blue-600 hover:bg-blue-700"
                                    data-testid="create-project-submit-btn"
                                >
                                    {creating ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Creating...
                                        </>
                                    ) : (
                                        'Create Project'
                                    )}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Search */}
                <div className="relative mb-6">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                        placeholder="Search projects..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 py-5 bg-white border-gray-200"
                        data-testid="search-projects-input"
                    />
                </div>

                {/* Projects Grid */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                ) : filteredProjects.length === 0 ? (
                    <div className="text-center py-20">
                        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                            <FolderOpen className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="font-heading text-lg text-gray-900 mb-2">
                            {searchQuery ? 'No projects found' : 'No projects yet'}
                        </h3>
                        <p className="text-gray-500 mb-6">
                            {searchQuery ? 'Try a different search term' : 'Create your first project to get started'}
                        </p>
                        {!searchQuery && (
                            <Button onClick={() => setCreateDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="empty-create-project-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Create Project
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredProjects.map((project) => (
                            <Card 
                                key={project.id}
                                className="bg-white border-gray-200 hover:border-blue-300 transition-colors cursor-pointer group"
                                onClick={() => navigate(`/editor/${project.id}`)}
                                data-testid={`project-card-${project.id}`}
                            >
                                <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className="w-8 h-8 rounded bg-blue-50 flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                                <Code2 className="w-4 h-4" />
                                            </div>
                                            <CardTitle className="font-heading text-base text-gray-900 line-clamp-1">
                                                {project.name}
                                            </CardTitle>
                                        </div>
                                        <CardDescription className="text-gray-500 line-clamp-2 text-sm">
                                            {project.description || 'No description'}
                                        </CardDescription>
                                    </div>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                            <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0">
                                                <MoreVertical className="w-4 h-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="bg-white border-gray-200">
                                            <DropdownMenuItem onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/editor/${project.id}`);
                                            }} className="text-gray-700">
                                                <Code2 className="w-4 h-4 mr-2" />
                                                Open Editor
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={(e) => {
                                                e.stopPropagation();
                                                openRenameDialog(project);
                                            }} className="text-gray-700">
                                                <Pencil className="w-4 h-4 mr-2" />
                                                Rename
                                            </DropdownMenuItem>
                                            {project.deployment_url && (
                                                <DropdownMenuItem onClick={(e) => {
                                                    e.stopPropagation();
                                                    window.open(project.deployment_url, '_blank');
                                                }} className="text-gray-700">
                                                    <ExternalLink className="w-4 h-4 mr-2" />
                                                    View Deployment
                                                </DropdownMenuItem>
                                            )}
                                            <DropdownMenuItem 
                                                className="text-red-600 focus:text-red-600"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteProject(project.id);
                                                }}
                                            >
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                Delete
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </CardHeader>
                                <CardContent className="pt-0">
                                    <div className="flex items-center justify-between text-xs text-gray-400">
                                        <div className="flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            <span>{formatDate(project.updated_at || project.created_at)}</span>
                                        </div>
                                        {project.deployment_status === 'deployed' && (
                                            <div className="flex items-center gap-1 text-green-600">
                                                <Rocket className="w-3 h-3" />
                                                <span>Live</span>
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </main>

            {/* Rename Project Dialog */}
            <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
                <DialogContent className="sm:max-w-[425px] bg-white">
                    <DialogHeader>
                        <DialogTitle>Rename Project</DialogTitle>
                        <DialogDescription>
                            Enter a new name for your project.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Label htmlFor="rename-name" className="text-right">
                            Project Name
                        </Label>
                        <Input
                            id="rename-name"
                            value={renameProject.name}
                            onChange={(e) => setRenameProject({ ...renameProject, name: e.target.value })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && renameProject.name.trim()) {
                                    handleRenameProject();
                                }
                            }}
                            className="mt-2"
                            placeholder="Enter project name"
                            autoFocus
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setRenameDialogOpen(false);
                                setRenameProject({ id: null, name: '' });
                            }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleRenameProject} 
                            disabled={!renameProject.name.trim() || renaming}
                        >
                            {renaming ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Renaming...
                                </>
                            ) : (
                                'Rename'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Dashboard;
